import React, {useEffect, useMemo, useState} from 'react'
import { Button, Header, Icon, Placeholder, Segment, Table } from 'semantic-ui-react'
import { useAppDispatch, useAppSelector } from '../../common/store'
import { APFrameDetailsType } from './ap.types'
import { removeFromCart, selectCartItems } from './apCart.slice'
import CheckoutModal from './APCheckout'
import {
  clearHoverFrame,
  clearSelectFrame,
  selectHoverFrame,
  selectSelectedFrame,
  setHoverFrame,
  setSelectFrame,
  setZoomExtentMode,
} from './apFrames.slice'
import PreviewImage from './PreviewImage'
import {AP_IMAGE_PATH, RECAPTCHA_ENABLED} from "../../environment";
import {APUtils} from "./ap.util";
import {generateRecaptchaToken} from "../../common/recapctcha";
import {getRecaptcha} from "../../common/slice";

type APCartProps = {
  setShowCart: (showCart: boolean) => void
}
const APCart = ({ setShowCart }: APCartProps) => {
  const dispatch = useAppDispatch()
  const cartItems = useAppSelector(selectCartItems)

  // Changes the zoom extent mode for the map when the component is mounted
  useEffect(() => {
    dispatch(setZoomExtentMode('cartFrames'))
  })

  return (
    <>
      <Segment attached="top">
        <div style={{
          display: 'flex',
          flexDirection: 'row'
        }}>
          <div style={{
            flexGrow: 0
          }}>
              <Icon name={'close'} link onClick={() => setShowCart(false)} />
          </div>
          <div style={{
            flexGrow: 1
          }}>
            <Header as="h3" textAlign={'center'} >
              Shopping Cart
            </Header>

          </div>
        </div>
      </Segment>
      <Segment attached="bottom"
               style={{
                 display: 'flex',
                 flexDirection: 'column',
                 flexGrow: 1,
                 flexShrink: 1,
                 overflow: 'hidden',
               }}>
        {cartItems.length > 0 &&
            <>
                <Table compact className={'apCartTable'}>
                    <Table.Header>
                        <Table.Row>
                            <Table.HeaderCell>Frame #</Table.HeaderCell>
                            <Table.HeaderCell>Project</Table.HeaderCell>
                            <Table.HeaderCell>Year</Table.HeaderCell>
                            <Table.HeaderCell textAlign={'center'}>Preview</Table.HeaderCell>
                            <Table.HeaderCell></Table.HeaderCell>
                        </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {cartItems.map(item => <CartItem frame={item.frame} key={item.frame.id}/>)}
                    </Table.Body>
                </Table>
                <div>
                    <CheckoutModal/>
                </div>
            </>
        }
        {cartItems.length === 0 &&
            <h4 style={{ textAlign: 'center' }}>
                Your cart is empty. Add items to the cart by searching for frames and clicking the "Add to Cart" button
                after selecting a frame.
            </h4>
        }
      </Segment>
    </>
  )
}


type CartItemProps = {
  frame: APFrameDetailsType
}


const CartItem = ({ frame }: CartItemProps) => {

  const dispatch = useAppDispatch()
  const selectedFrame = useAppSelector(selectSelectedFrame)
  const hoverFrame = useAppSelector(selectHoverFrame)
  const [ recaptchaV3Key, setRecaptchaV3Key] = useState<string>("")

  const width = 100
  const height = 100
  const quality = 100

  useEffect( () => {
    if (RECAPTCHA_ENABLED) {
      generateRecaptchaToken().then((res) => setRecaptchaV3Key(res));
    }
  }, [])

  const LoadingElement = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: width, height: height }}>
        <Placeholder style={{ width: width, height: height }}>
          <Placeholder.Image/>
        </Placeholder>
      </div>
    )
  }

  const ErrorElement = () => {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', width: width, height: height }}>
        <div>
          <h4 style={{ textAlign: 'center' }}>No digitised image available</h4>
        </div>
      </div>
    )
  }

  const handleClickRemove = () => {
    dispatch(removeFromCart(frame))
  }

  const onClick = () => {
    if (!selectedFrame || selectedFrame.id !== frame.id) {
      dispatch(setSelectFrame(frame))
    } else {
      dispatch(clearSelectFrame())
    }
  }

  const onMouseOver = () => {
    dispatch(setHoverFrame(frame))
  }

  const onMouseOut = () => {
    if (hoverFrame && hoverFrame === frame) {
      dispatch(clearHoverFrame())
    }
  }

  const cssClasses = useMemo(() => {
    let cssClasses = 'apDataTableRow'
    if (selectedFrame === frame) {
      cssClasses += ' apDataTableRowSelected'
    }
    return cssClasses
  }, [frame, selectedFrame])

  return (
    <Table.Row className={cssClasses}
               onClick={onClick}
               onMouseOver={onMouseOver}
               onMouseOut={onMouseOut}>
      <Table.Cell>
        {frame.frame}
      </Table.Cell>
      <Table.Cell>
        {frame.project}
      </Table.Cell>
      <Table.Cell>
        {frame.year}
      </Table.Cell>
      <Table.Cell textAlign={'center'}>
        <PreviewImage
          key={APUtils.imgSrcUrl({selectedFrameDetails: frame, width: width, height: height, quality: quality, recaptchaToken: recaptchaV3Key})}
          src={APUtils.imgSrcUrl({selectedFrameDetails: frame, width: width, height: height, quality: quality, recaptchaToken: recaptchaV3Key})}
          loadingElement={<LoadingElement/>}
          errorElement={<ErrorElement/>}
        />
      </Table.Cell>
      <Table.Cell textAlign={'center'}>
        <Button icon onClick={handleClickRemove} size={'large'}>
          <Icon name={'trash'}/>
        </Button>
      </Table.Cell>
    </Table.Row>
  )
}

export default APCart